import { IconButton } from '@vrplatform/components';
import {
  isProductionEnvironment,
  isStagingEnvironment,
  useApiEndpoint,
  useTeam,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  Button as MantineButton,
  Indicator,
  Menu,
  Stack,
  Text,
} from '@mantine/core';
import { useNavbarExpanded } from '../../../hooks';

const API_OPTIONS = [
  { label: 'Production', value: 'api.vrplatform.app' },
  { label: 'Staging', value: 'staging.api.vrplatform.app' },
  { label: 'Local', value: 'localhost:8877' },
  { label: 'Edge (Staging)', value: 'staging.api.edge.vrplatform.app' },
] as const;

type ApiOption = (typeof API_OPTIONS)[number];

export const NavbarApiSwitcher = () => {
  const [{ isGL: GL }] = useTeam();
  const { isDesktopExpanded } = useNavbarExpanded();
  const { apiHost, setApiHost } = useApiEndpoint();

  // Each portal lane talks only to its own API (PRO-20018).
  const apiOptions = isProductionEnvironment()
    ? API_OPTIONS.filter(
        (option) => option.value !== 'staging.api.vrplatform.app'
      )
    : isStagingEnvironment()
      ? API_OPTIONS.filter((option) => option.value !== 'api.vrplatform.app')
      : API_OPTIONS;
  const activeOption = apiOptions.find((option) => option.value === apiHost);
  const activeLabel = activeOption?.label ?? apiHost;

  const handleSelect = (option: ApiOption) => {
    if (apiHost === option.value) return;
    setApiHost(option.value);
  };

  const indicator =
    apiHost === 'localhost:8877'
      ? { label: 'LOCAL', color: 'red' }
      : apiHost === 'staging.api.edge.vrplatform.app'
        ? { label: 'EDGE', color: 'orange' }
        : null;

  const target = GL ? (
    <IconButton
      icon="GlobeIcon"
      onClick={undefined}
      tooltip={`API: ${activeLabel}`}
      color={(theme) => theme.colors.neutral[3]}
    />
  ) : (
    <MantineButton
      variant="light"
      color="white"
      sx={{
        background: 'none',
        border: 'none',
        height: 40,
        '&:disabled': {
          background: 'transparent',
        },
      }}
      fw={500}
      radius={'md'}
      styles={{
        label: isDesktopExpanded
          ? {
              marginRight: 'auto',
              opacity: '1 !important',
            }
          : {
              fontSize: 16,
              opacity: '1 !important',
            },
        rightIcon: {
          fontSize: 16,
        },
      }}
      px="xs"
      mx="xs"
      mb={4}
      size="xs"
      leftSection={
        isDesktopExpanded && (
          <Icon icon="GlobeIcon" size={16} color={(theme) => theme.white} />
        )
      }
      rightSection={
        isDesktopExpanded && (
          <Icon icon="ChevronIcon" size={16} color={(theme) => theme.white} />
        )
      }
    >
      {isDesktopExpanded ? (
        `API: ${activeLabel}`
      ) : (
        <Icon icon="GlobeIcon" size={16} color={(theme) => theme.white} />
      )}
    </MantineButton>
  );

  return (
    <Menu position="top-start" shadow="lg" radius="md" withinPortal width={260}>
      <Menu.Target>
        {indicator ? (
          <Indicator
            inline
            label={indicator.label}
            color={indicator.color}
            size={22}
            offset={6}
            radius="sm"
            withBorder
            styles={{
              indicator: {
                fontWeight: 700,
                fontSize: 10,
                letterSpacing: 0.6,
                paddingInline: 6,
                textTransform: 'uppercase',
              },
            }}
          >
            {target}
          </Indicator>
        ) : (
          target
        )}
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>API Environment</Menu.Label>
        {apiOptions.map((option) => {
          const isActive = option.value === apiHost;
          return (
            <Menu.Item
              key={option.value}
              onClick={() => handleSelect(option)}
              leftSection={
                <Icon
                  icon={isActive ? 'CheckIcon' : 'GlobeIcon'}
                  size={16}
                  color={(theme) => theme.black}
                />
              }
            >
              <Stack gap={0}>
                <Text size="sm">{option.label}</Text>
                <Text size="xs" c="dimmed">
                  {option.value}
                </Text>
              </Stack>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};
